import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Card, ConfirmDialog, ErrorBanner, PageHeader, useToast } from '@zarya/ui';
import { useCanOperar } from '@app/auth/useCan';
import { t, tn } from '@app/i18n';
import { TAB_PARAM } from '@app/navigation';
import { IdentityListPane } from '@app/shared/IdentityListPane';
import painel from '@app/shared/IdentityListPane/IdentityListPane.module.css';
import { useIdentities } from '../Contracts/Contracts.api';
import { useAccessControl, useSubmitCandidate } from './AccessControl.api';
import { sendMessage, withoutContract } from './AccessControlModel';
import { DirtyFooter } from './DirtyFooter';
import { IncomingTab } from './IncomingTab';
import { OutgoingTab } from './OutgoingTab';
import { ToolsTab } from './ToolsTab';
import { useAccessControlDraft } from './useAccessControlDraft';
import { ViaPoliciesTab } from './ViaPoliciesTab';
import s from './AccessControl.module.css';

type Tab = 'tools' | 'outgoing' | 'incoming' | 'policies';

/**
 * Fase 2: a aba que a URL pede (`?tab=`). É como «Quem pode invocar» (T7) abre a Saída A2A de quem chama e como
 * «Recebe A2A» (T5) abre a Entrada: o link leva à aba que responde a pergunta, e não à primeira.
 */
const TABS: readonly Tab[] = ['tools', 'outgoing', 'incoming', 'policies'];
function tabOf(value: string | null): Tab {
  return TABS.includes(value as Tab) ? (value as Tab) : 'tools';
}

/**
 * T14 — Identity Access (FR-071…FR-078).
 *
 * "Quem pode invocar esta identidade, o que ela pode invocar e quais tools pode
 * usar, com que limite. Salvar não aplica nada — gera um contrato candidato que
 * vai para Aprovações."
 *
 * A identidade se escolhe na lista à esquerda — o mesmo `IdentityListPane` de
 * T13, mesmo visual e mesmo comportamento (pedido do mrolim, 28/08). O que era
 * do combobox e continua valendo: `?identidade=` guarda a escolha e a FR-071
 * desabilita quem não tem contrato ativo, com o motivo em vermelho na linha.
 */
export function AccessControlPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [tab, setAba] = useState<Tab>(() => tabOf(searchParams.get(TAB_PARAM)));
  const [trocaPendente, setTrocaPendente] = useState<string | null>(null);

  const canSave = useCanOperar('submitAccessControlCandidate');
  const identidades = useIdentities();
  const lista = identidades.data ?? [];
  const withContract = lista.filter((i) => i.active_contract_version);
  const identityURL = searchParams.get('identidade');
  const selecionada = identityURL ?? withContract[0]?.id ?? null;

  const estado = useAccessControl(selecionada);
  const rascunho = useAccessControlDraft(estado.data);
  const send = useSubmitCandidate();

  const invalid = Object.keys(rascunho.erros).length > 0;

  const applySwap = (id: string | null) => {
    const proximos = new URLSearchParams(searchParams);
    if (id) proximos.set('identidade', id);
    else proximos.delete('identidade');
    setSearchParams(proximos, { replace: true });
    setTrocaPendente(null);
  };

  /** Trocar de identidade com rascunho pede confirmação (FR-077, FR-131). */
  const choose = (id: string | null) => {
    if (rascunho.mudancas > 0 && id !== selecionada) setTrocaPendente(id);
    else applySwap(id);
  };

  const save = () => {
    if (!selecionada) return;
    send.mutate(
      { id: selecionada, body: rascunho.paraEnvio() },
      {
        onSuccess: (resultado) => {
          if (resultado.no_authority_change) {
            toast.mostrar(t('identityAccess.toast.noAuthorityChange'), { tone: 'info' });
            return;
          }
          toast.mostrar(sendMessage(resultado), { tone: 'sucesso' });
          // FR-078: o candidato vira pendência — a decisão acontece em T10. Vale
          // também quando a plataforma já a fechou: o drawer é onde se vê o que
          // ela decidiu e qual contrato saiu.
          navigate(`/aprovacoes?drawer=${resultado.approval?.id ?? ''}`);
        },
      },
    );
  };

  const withoutActiveContract = estado.error?.code === 'sem_contrato_ativo';

  return (
    <>
      {/* «Access Control» virou «Identity Access» em 03/09: é o eixo da
          **identidade** — o que cada workload alcança. O outro eixo, o da
          pessoa, é Human Access (T11). O item-pai «Controle de acesso» é só o
          rótulo do grupo no menu, sem tela própria (12/10/2026). */}
      <PageHeader
        title={t('identityAccess.page.title')}
        tip={t('identityAccess.page.tip')}
        icon="key"
      />

      {/*
        Fica na tela, não no ⓘ: quem vai clicar em Salvar precisa saber **antes**
        que o botão não aplica nada. Descrição vai para o tooltip; o que muda a
        decisão de quem opera, não.
      */}
      <Alert tone="info" title={t('identityAccess.save.notice.title')}>
        {t('identityAccess.save.notice.body')}
      </Alert>

      {identidades.isError ? (
        <ErrorBanner
          error={identidades.error}
          title={t('identityAccess.identitiesError')}
          onRetry={() => void identidades.refetch()}
          hideLink
        />
      ) : null}

      {!identidades.isLoading && !withContract.length ? (
        <Card title={t('identityAccess.noIdentities.title')}>
          <p className={s.carregando}>
            {t('identityAccess.noIdentities.body', { withoutContract: withoutContract() })}
          </p>
        </Card>
      ) : null}

      {identidades.isLoading || lista.length > 0 ? (
        <div className={painel.layout}>
          <IdentityListPane
            identidades={lista}
            selecionada={selecionada}
            onEscolher={choose}
            carregando={identidades.isLoading}
            tip={t('identityAccess.listPane.tip')}
            // FR-071: sem contrato ativo a identidade aparece, mas desabilitada —
            // e o motivo vai em vermelho na linha (ajustes-front #9).
            motivoIndisponivel={(identidade) =>
              identidade.active_contract_version ? null : withoutContract()
            }
          />

          <div className={painel.detalhe}>
            {withoutActiveContract ? (
              <Alert tone="warning" title={t('identityAccess.withoutContract.title')}>
                {t('identityAccess.withoutContract.body', { withoutContract: withoutContract() })}
              </Alert>
            ) : estado.isError ? (
              <ErrorBanner
                error={estado.error}
                title={t('identityAccess.readError')}
                onRetry={() => void estado.refetch()}
                hideLink
              />
            ) : null}

            {send.error ? (
              <ErrorBanner
                error={send.error}
                escrita
                title={t('identityAccess.sendError')}
                onRetry={save}
              />
            ) : null}

            {estado.isLoading ? (
              <Card title={t('identityAccess.page.title')}>
                <p className={s.carregando}>{t('identityAccess.loading.body')}</p>
              </Card>
            ) : null}

            {estado.data ? (
              <>
                <Card bare>
                  <div className={s.tabs} role="tablist" aria-label={t('identityAccess.tabs.aria')}>
                    <Tab
                      atual={tab}
                      value="tools"
                      onClick={setAba}
                      // A contagem inclui as que a grade não desenha: elas são
                      // concedidas pelo contrato e valem no PDP. Sem elas o
                      // laboratório mostrou «Tools · 0 concedidas» sobre um agente
                      // com uma tool autorizada — a mesma mentira por omissão que o
                      // bloco de aviso existe para desfazer, na aba ao lado.
                      label={t('identityAccess.tab.tools', {
                        n:
                          Object.values(rascunho.draft.tools).filter((tool) => tool.concedida)
                            .length + (estado.data.out_of_catalog?.length ?? 0),
                      })}
                    />
                    <Tab
                      atual={tab}
                      value="outgoing"
                      onClick={setAba}
                      label={t('identityAccess.tab.outgoing', {
                        n: Object.values(rascunho.draft.a2a).filter(Boolean).length,
                      })}
                    />
                    <Tab
                      atual={tab}
                      value="incoming"
                      onClick={setAba}
                      label={t('identityAccess.tab.incoming', {
                        n: estado.data.receives_a2a
                          ? estado.data.incoming.filter((i) => i.can_invoke).length
                          : 0,
                      })}
                    />
                    <Tab
                      atual={tab}
                      value="policies"
                      onClick={setAba}
                      label={t('identityAccess.tab.viaHumanAccess', {
                        n: estado.data.via_policies.length,
                      })}
                    />
                  </div>

                  {tab === 'tools' ? (
                    <ToolsTab estado={estado.data} rascunho={rascunho} editavel={canSave} />
                  ) : null}
                  {tab === 'outgoing' ? (
                    <OutgoingTab estado={estado.data} rascunho={rascunho} editavel={canSave} />
                  ) : null}
                  {tab === 'incoming' ? (
                    <IncomingTab estado={estado.data} onEditarA2ADe={choose} />
                  ) : null}
                  {tab === 'policies' ? <ViaPoliciesTab estado={estado.data} /> : null}
                </Card>

                <DirtyFooter
                  mudancas={rascunho.mudancas}
                  contratoVersion={estado.data.contract_version}
                  invalido={invalid}
                  enviando={send.isPending}
                  podeSalvar={canSave}
                  onDescartar={() => {
                    rascunho.descartar();
                    toast.mostrar(t('identityAccess.toast.discarded'), { tone: 'info' });
                  }}
                  onSalvar={save}
                />
              </>
            ) : null}
          </div>
        </div>
      ) : null}

      <ConfirmDialog
        open={trocaPendente !== null}
        title={t('identityAccess.confirm.title')}
        message={tn('identityAccess.confirm.message', rascunho.mudancas)}
        confirmLabel={t('identityAccess.confirm.label')}
        destructive
        onConfirm={() => applySwap(trocaPendente)}
        onCancel={() => setTrocaPendente(null)}
      />
    </>
  );
}

function Tab({
  atual,
  value,
  label,
  onClick,
}: {
  atual: Tab;
  value: Tab;
  label: string;
  onClick: (tab: Tab) => void;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={atual === value}
      className={[s.tab, atual === value ? s.tabOn : ''].filter(Boolean).join(' ')}
      onClick={() => onClick(value)}
    >
      {label}
    </button>
  );
}
